import { type RefObject, useCallback, useEffect } from 'react';

import { useAudioPlayerStreamingContext } from '../context/AudioPlayerStreamingProvider';
import { setActiveContent } from '../state/AudioPlayerState';
import type { PlaylistItemData } from '../ui/playlist/PlaylistItem';

type UsePlaylistAutoPlayProps = {
	audioRef: RefObject<HTMLAudioElement>;
	playlistItems: PlaylistItemData[];
	currentItemId: string | undefined;
};

export const usePlaylistAutoPlay = ({
	audioRef,
	playlistItems,
	currentItemId,
}: UsePlaylistAutoPlayProps) => {
	const { fetchAudio } = useAudioPlayerStreamingContext();
	const playNextTrack = useCallback(() => {
		if (playlistItems.length === 0) {
			return;
		}

		const currentIndex = playlistItems.findIndex((item) => item.id === currentItemId);

		let nextTrack: PlaylistItemData;

		if (currentIndex === -1) {
			nextTrack = playlistItems[0];
		} else if (currentIndex === playlistItems.length - 1) {
			return;
		} else {
			nextTrack = playlistItems[currentIndex + 1];
		}

		setActiveContent({
			contentId: nextTrack.id,
			spaceKey: nextTrack.spaceKey || '',
			contentType: nextTrack.contentType || '',
			title: nextTrack.title || '',
			author: nextTrack.author || '',
			avatarUrl: '',
		});

		fetchAudio();
	}, [currentItemId, playlistItems, fetchAudio]);

	useEffect(() => {
		const audio = audioRef.current;

		if (!audio) {
			return;
		}

		const onEnded = () => {
			playNextTrack();
		};

		audio.addEventListener('ended', onEnded);

		return () => {
			audio.removeEventListener('ended', onEnded);
		};
	}, [audioRef, playNextTrack]);
};
